import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Plyr from 'plyr';
import SplitType from 'split-type';
import Popup from '../components/popup';

export default class Block02 {
    constructor (query) {
        const el = document.querySelector(query);
        if (!el) return;

        this.el = el;
        this.gsap = gsap;

        this.init();
    }

    init () {
        this.gsap.registerPlugin(ScrollTrigger);

        this.popup();
        this.animation();
    }

    popup () {
        const trigger = this.el.querySelector('#credentials-trigger');
        if (!trigger) return;

        const popup = this.el.querySelector('.block-02--popup');
        if (!popup) return;

        const window = popup.querySelector('.site-popup-window');
        const close = popup.querySelector('.site-popup-close');

        new Popup({ popup, toggle: trigger, window, close });
    }

    videos () {
        const background = this.el.querySelector('.block-02--background-video video');
        const foreground = this.el.querySelector('.block-02--foreground-video video');

        const videos = { background: null, foreground: null }

        if (background) {
            const player = new Plyr(background, { controls: [], quality: { default: 1080 } });

            player.volume = 0;

            if (window.innerWidth < 768) player.play();

            videos.background = player;
        } else {
            videos.background = null;
        }

        if (foreground) {
            const player = new Plyr(foreground, { controls: [], quality: { default: 1080 } });

            player.volume = 0;

            videos.foreground = player;
        } else {
            videos.foreground = null;
        }

        return videos;
    }

    animation () {
        const videos = this.videos();

        const about = this.el.querySelector('[data-animation="about"]');

        const title = this.el.querySelector('.site-section--heading .site-title');
        const text = this.el.querySelector('.site-section--heading .site-text');
        const paragraphs = text.querySelectorAll('p');
        const items = this.el.querySelectorAll('.block-02--list-item');
        const button = this.el.querySelector('.block-02--controls');

        if (window.innerWidth > 767) {
            new SplitType(title, { types: 'lines, words' });

            [...paragraphs].forEach((paragraph) => {
                new SplitType(paragraph, { types: 'lines, words' });
            });
        }

        setTimeout(() => {
            const hLines = about.querySelectorAll('.site-section--heading .site-title .word');
            const tLines = about.querySelectorAll('.site-section--heading .site-text .word');

            /* Step: Video */

            const waitForVideo = (video) => new Promise((resolve) => {
                if (!video) resolve();

                const el = video.elements.original;

                if (el.readyState >= 4) resolve();

                el.addEventListener('canplaythrough', resolve, { once: true });
            });

            if (window.innerWidth > 767) {
                Promise.all([
                    waitForVideo(videos.background),
                    waitForVideo(videos.foreground)
                ]).then(() => {
                    this.gsap.timeline({
                        scrollTrigger: {
                            trigger: this.el,
                            start: () => 'top bottom',
                            onEnter: () => {
                                if (videos.background) videos.background.play();
                                if (videos.foreground) videos.foreground.play();
                            },
                            onLeave: () => {
                                if (videos.background) videos.background.pause();
                                if (videos.foreground) videos.foreground.pause();
                            },
                            onEnterBack: () => {
                                if (videos.background) videos.background.play();
                                if (videos.foreground) videos.foreground.play();
                            },
                            onLeaveBack: () => {
                                if (videos.background) videos.background.pause();
                                if (videos.foreground) videos.foreground.pause();
                            },
                            scrub: true
                        }
                    });
                });

                /* Step: Heading */

                {
                    let start = `top bottom-=${window.innerHeight * 0.35}px`;
                    let end = `+=${window.innerHeight * 0.65}px`;

                    if (window.innerWidth < 1200) {
                        start = 'top bottom';
                        end = `+=${window.innerHeight * 0.85}px`;
                    }

                    const timeline = this.gsap.timeline({
                        scrollTrigger: {
                            trigger: this.el,
                            start: () => start,
                            end: () => end,
                            scrub: true
                        }
                    });

                    if (hLines[0]) timeline.to(hLines[0], { duration: 1, y: 0 });

                    for (let i = 1; i < 100; i++) {
                        if (hLines[i]) timeline.to(hLines[i], { duration: 1, y: 0 }, '<');
                    }
                }

                /* Step: Description */

                {
                    let start = `top bottom-=${window.innerHeight * 0.75}px`;
                    let end = `+=${window.innerHeight * 0.75}px`;

                    if (window.innerWidth < 1200) {
                        start = 'top bottom';
                        end = `+=${window.innerHeight * 0.85}px`;
                    }

                    const timeline = this.gsap.timeline({
                        scrollTrigger: {
                            trigger: this.el,
                            start: () => start,
                            end: () => end,
                            scrub: true
                        }
                    });

                    if (tLines[0]) timeline.to(tLines[0], { duration: 1, y: 0 });

                    for (let i = 1; i < 300; i++) {
                        if (tLines[i]) timeline.to(tLines[i], { duration: 1, y: 0 }, '<');
                    }
                }

                /* Step: Items */

                {
                    const timeline = this.gsap.timeline({
                        scrollTrigger: {
                            trigger: items[0],
                            start: () => `top bottom+=${items[0].offsetHeight}px`,
                            end: () => `+=${items[0].offsetHeight * 2}px`,
                            scrub: true
                        }
                    });

                    timeline
                        .to(items[0], { duration: 1, y: 0, opacity: 1 })
                        .to(items[1], { duration: 1, y: 0, opacity: 1 }, '<')
                        .to(items[2], { duration: 1, y: 0, opacity: 1 }, '<');
                }

                /* Step: Button */

                {
                    const timeline = this.gsap.timeline({
                        scrollTrigger: {
                            trigger: button,
                            start: () => 'top bottom',
                            end: () => `+=${button.offsetHeight * 5}px`,
                            scrub: true
                        }
                    });

                    timeline
                        .to(button, { duration: 1, opacity: 1 });
                }
            } else {
                title.classList.add('site-animation', 'fade-up');
                text.classList.add('site-animation', 'fade-up');

                [...items].forEach((item) => item.classList.add('site-animation', 'fade-up'));

                button.classList.add('site-animation', 'fade-in');

                [title, text, ...items, button].forEach((element) => {
                    gsap.timeline({
                        scrollTrigger: {
                            trigger: element,
                            start: 'top bottom',
                            once: true,
                            onEnter: () => element.classList.add('animated')
                        }
                    });
                });
            }
        });
    }
}
